/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, July 7th 2024, 8:31:34 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import StyleIcon from "@mui/icons-material/Style";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useContentTagsAPI } from "common/services/ContentTagsAPI";
import { getDefaultContentTag } from "lib/factories";
import { ContentTag, Organization } from "lib/schemas";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import {
  DataTable,
  StyledOSDialog,
  StackedSelectionCard,
  OSActionButton,
  OSAPIForm,
  TextFormInput,
  useToggleState,
  TextAreaFormInput,
  OSAlert,
  APIListProgressWrapper,
  EmptyStateActionWrapper,
  Image,
} from "ui-widgets";

type ContentTagsListProps = {
  selectedTagIds?: ContentTag["id"][];
  onSelectContentTag?: (contentTag: ContentTag) => void;
  onDeselectContentTag?: (contentTag: ContentTag) => void;
  organizationId: Organization["id"];
};

export const ContentTagsList = (props: ContentTagsListProps) => {
  const [contentTags, setContentTags] = React.useState<ContentTag[]>([]);
  const isSelected = (contentTag: ContentTag) => props.selectedTagIds?.includes(contentTag.id);

  const onAddNewTag = (contentTag: ContentTag) => {
    setContentTags((previous) => [...previous, contentTag]);
  };

  const onEditExistingTag = (contentTag: ContentTag) => {
    setContentTags((previous) => previous.map((tag) => (tag.id === contentTag.id ? contentTag : tag)));
  };
  const onDeleteTag = (contentTagId: ContentTag["id"]) => {
    setContentTags((previous) => previous.filter((tag) => tag.id !== contentTagId));
  };

  const { useFetchContentTagsAPI } = useContentTagsAPI();
  const { fetchContentTags, isAPIError, httpResponseStatusCode, isMakingAPIRequest } = useFetchContentTagsAPI();

  React.useEffect(() => {
    fetchContentTags(setContentTags);
  }, [fetchContentTags]);

  const newTagFormpopupState = useToggleState();
  const newContentTagComponent = (
    <>
      <OSActionButton onClick={newTagFormpopupState.handleOpen} variant="contained" sx={{ ml: "auto" }}>
        Add New Tag
      </OSActionButton>
      <ContentTagForm
        contentTagId={null}
        organizationId={props.organizationId}
        open={newTagFormpopupState.open}
        onClose={newTagFormpopupState.handleClose}
        onSelectTag={(contentTag) => {
          onAddNewTag(contentTag);
          props?.onSelectContentTag?.(contentTag);
        }}
      />
    </>
  );

  return (
    <Stack spacing={2}>
      <APIListProgressWrapper
        items={contentTags}
        isAPIError={isAPIError}
        httpResponseStatusCode={httpResponseStatusCode}
        showLoader={isMakingAPIRequest}
        noDataMessage={<EmptyContentTagsListCTA>{newContentTagComponent}</EmptyContentTagsListCTA>}
      >
        {newContentTagComponent}
        <div className="os-org-list-wrapper">
          <DataTable<ContentTag>
            title="Content Tags"
            dataSource={contentTags}
            apiFilterParams={{ ids: [] }}
            defaultOrderBy="slug"
            searchKeys={["name", "slug"]}
            titleKey="name"
            uniqueKey="id"
            listViewMode="grid"
            renderGridItem={(contentTag) => (
              <ContentTagGridItem
                contentTag={contentTag}
                isSelected={isSelected(contentTag)}
                onSelectContentTag={props.onSelectContentTag}
                onDeselectContentTag={props.onDeselectContentTag}
                onEditTag={onEditExistingTag}
                onDeleteTag={onDeleteTag}
              />
            )}
            showToolbar={false}
            {...props}
          />
        </div>
      </APIListProgressWrapper>
    </Stack>
  );
};

const ContentTagGridItem = ({
  contentTag,
  onDeleteTag,
  onEditTag,
  ...props
}: {
  contentTag: ContentTag;
  isSelected: boolean;
  onEditTag?: (t: ContentTag) => void;
  onDeleteTag?: (tId: ContentTag["id"]) => void;
} & Pick<ContentTagsListProps, "onSelectContentTag" | "onDeselectContentTag">) => {
  const editTagFormpopupState = useToggleState();
  const { useDeleteContentTag } = useContentTagsAPI();

  return (
    <>
      <StackedSelectionCard
        label={
          <h4>
            {contentTag.name} [{contentTag.slug}]
          </h4>
        }
        onClick={() =>
          props.isSelected ? props.onDeselectContentTag?.(contentTag) : props.onSelectContentTag?.(contentTag)
        }
        actionButton={
          <Stack direction="row">
            <IconButton
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                editTagFormpopupState.handleOpen();
              }}
            >
              <EditIcon />
            </IconButton>
            <div onClick={(event) => event.stopPropagation()}>
              <DeleteOSItemDialog<ContentTag>
                itemId={contentTag.id}
                useDeleteOSItem={useDeleteContentTag}
                onDelete={() => {
                  onDeleteTag?.(contentTag.id);
                  props.onDeselectContentTag(contentTag);
                }}
                onRestore={(restoredContentTag) => {}}
                dialogTitle="Delete this tag?"
                dialogMessage={`Deleting the tag named '${contentTag.name}' will detach it from any other content that was using it. Are you sure about this?`}
              >
                {({ handleOpen }) => (
                  <IconButton color="error" onClick={handleOpen}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </DeleteOSItemDialog>
            </div>
            <Tooltip title={<Typography>{props.isSelected ? "Detach Tag" : "Attach Tag"}</Typography>}>
              <IconButton color="primary">{props.isSelected ? <RemoveCircleIcon /> : <AddCircleIcon />}</IconButton>
            </Tooltip>
          </Stack>
        }
        icon={<StyleIcon color="primary" sx={{ height: "100%" }} />}
        isSelected={props.isSelected}
      />
      <ContentTagForm
        contentTagId={contentTag.id}
        organizationId={contentTag.organization}
        open={editTagFormpopupState.open}
        onClose={editTagFormpopupState.handleClose}
        onTagAPIItemCallBack={onEditTag}
      />
    </>
  );
};

type ContentTaggingPopupProps = Required<Pick<React.ComponentProps<typeof StyledOSDialog>, "open" | "onClose">> &
  Pick<ContentTagsListProps, "selectedTagIds" | "onSelectContentTag" | "onDeselectContentTag" | "organizationId"> & {};

export const ContentTaggingPopup = ({ open, onClose, ...dtProps }: ContentTaggingPopupProps) => {
  return (
    <StyledOSDialog popUpIcon={<StyleIcon color="primary" />} popUpTitle="Tagging" open={open} onClose={onClose}>
      <ContentTagsList {...dtProps} />
    </StyledOSDialog>
  );
};

export const ContentTagForm = ({
  contentTagId,
  organizationId,
  open,
  onClose,
  onSelectTag,
  onTagAPIItemCallBack,
}: Required<Pick<React.ComponentProps<typeof StyledOSDialog>, "open" | "onClose">> & {
  contentTagId: ContentTag["id"];
  organizationId: Organization["id"];
  onSelectTag?: (contentTag: ContentTag) => void;
  onTagAPIItemCallBack?: (contentTag: ContentTag) => void;
}) => {
  const newContentTagDefault = getDefaultContentTag(organizationId);
  const { useContentTagsAPIForm } = useContentTagsAPI();
  const useContentTagAPIFormReturn = useContentTagsAPIForm({
    contentTagId,
    newContentTagDefault,
    updateContentTagFromAPICallback: (contentTag: ContentTag) => {
      onClose(null, "backdropClick");
      onSelectTag?.(contentTag);
      onTagAPIItemCallBack?.(contentTag);
    },
  });

  return (
    <StyledOSDialog
      open={open}
      onClose={onClose}
      popUpIcon={<StyleIcon color="primary" />}
      popUpTitle="Content Tag Form"
    >
      {useContentTagAPIFormReturn.apiDataSendErrors.non_field_errors && (
        <OSAlert severity="error">
          {useContentTagAPIFormReturn.apiDataSendErrors.non_field_errors.map((non_field_error) => (
            <>
              {non_field_error}
              <br />
            </>
          ))}
        </OSAlert>
      )}
      <OSAPIForm<ContentTag> useAPIFormReturn={useContentTagAPIFormReturn}>
        <TextFormInput<ContentTag> schema_field_key="name" label="Name" />
        <TextFormInput<ContentTag> schema_field_key="slug" label="Slug" />
        <TextAreaFormInput<ContentTag> schema_field_key="description" label="Description" />
      </OSAPIForm>
    </StyledOSDialog>
  );
};

export const EmptyContentTagsListCTA = ({ children }) => {
  return (
    <EmptyStateActionWrapper
      title={<>No Content Tags</>}
      description={
        <Typography>
          You have not created any content tags yet for your organization. Content tags allow for tagging of any type of
          content in onestop, including web content pages, workflow task templates etc..
        </Typography>
      }
      illustrationGraphic={<Image src="/img/content.png" alt="Onestop Admin" />}
    >
      {children}
    </EmptyStateActionWrapper>
  );
};
